/*
  bc-fit-finder — "what size am i?" size finder for the PDP.

  WHAT THIS IS AND IS NOT
  -----------------------
  It is a DETERMINISTIC engine, not a model. Every answer is a fixed lookup
  against the garment's own measurements. Nothing is sent anywhere, there is no
  API key, and it cannot invent a size. Do not label it AI in the copy.

  Every question is multiple choice on purpose. A free-text height or weight
  field on a phone is the single biggest drop-off point in a flow like this, and
  a typed number is not more accurate than a band once it is fed through an
  estimate anyway.

  USUAL SIZE IS THE ANCHOR. READ THIS BEFORE CHANGING ANYTHING.
  ------------------------------------------------------------
  The first build of this engine drove everything off the tape and it was WRONG.
  On paper this blank is narrow: the L is 105cm around the chest, where a Bella
  3001 L is about 112cm. A pure measurement model therefore tells every L-buyer
  to take an XL. The operator, who owns and wears the garment, confirmed that a
  normal US L-buyer takes an L here and it fits fine.

  So the tape is a TIE-BREAKER, not the driver. The size someone already owns
  and wears is a real-world measurement of their body taken by a garment they
  can feel; a chest estimated from a height band and a weight band is not.
  When the two disagree, the garment they own wins.

  THE MATH, IN ORDER
  ------------------
  1. BASE = the size they say they usually buy, mapped onto this ladder.
     Anyone who buys below the smallest size stocked lands on the smallest.

  2. Estimate the wearer's CHEST from height band + weight band + build.
     See estChest below for the anchors and why they were re-cut.
     This gives a SECOND opinion, used two ways:
       - if no usual size was given, it becomes the base
       - if it disagrees with the usual size by two or more steps, the usual
         size is moved ONE step toward it, never all the way

  3. Turn the estimate into a size by ease. Garment chest is measured FLAT, so
     circumference is double the chart value. A size serves a body chest of
     (circumference - ease_cm). At the calibrated 4cm this is:
       M 96 / L 101 / XL 105 / 2XL 110 / 3XL 114
     Ease 4 is what makes an AVERAGE 5ft9 build land on M, which the operator
     confirms is how this blank actually fits. M is a NORMAL size here, not a
     small one - that is the whole reason the number is this tight.

  4. Apply a LENGTH FLOOR from height. This blank runs short - 66cm on an M -
     so a tall wearer in their usual size gets a crop top. The floor is the
     smallest size whose body length >= height * length_factor (0.375 default).
     It can only ever push UP, and only by one size, so a tall slim person is
     never buried in a tent. When it fires, the result says so out loud.

  5. Fit preference shifts +/- from there. Fitted -1, regular 0, relaxed +1,
     oversized +2.

  6. If the estimate runs past the top of the range, 3XL is still returned but
     the copy says it will sit closer to the body than the photos. Silently
     handing someone the largest size and letting them find out is a return.
     NOTE: at ease 4 the 3XL tops out around a 45in chest. That is a real limit
     of the range, not a bug in the engine.

  BORDERLINE
  ----------
  If the chest estimate sits within border_cm of a boundary, or the length floor
  disagreed with the chest size, BOTH sizes are shown with a line on how each
  will feel. Operator's call: at 4-5cm between sizes on this blank, pretending
  to be certain would be the dishonest option.

  SELECTING THE SIZE AND ADDING IT
  --------------------------------
  Fabric renders every size as a real radio input inside variant-picker:
    <input type="radio" data-option-name="Size" value="XL">
  Clicking that input runs the picker's OWN change handler, which updates the
  price, the media and the variant id. Do not try to set the variant directly -
  you would desync the form.

  A PRODUCT CAN HAVE MORE THAN ONE SIZE GROUP. The 2-Pack sells a black shirt
  and a pink shirt in one purchase, so it carries two independent size ladders
  whose options are named after the colourways rather than "Size". The finder
  recommends ONE size and applies it to every group; the shopper is free to
  change either afterwards. See sizeGroupNames below.

  The final CTA then clicks the theme's REAL add-to-cart button, which carries
  on:click="/handleClick". That reuses the theme's submit, cart drawer,
  fly-to-cart animation and error handling. Do NOT post to /cart/add.js here -
  it would drift from the theme the first time any of that changes.

  MEMORY
  ------
  The result is kept in localStorage under bcff_v1 so the next product greets
  them with it. It is per-browser, never leaves the device, and is skipped
  entirely if the URL already carries ?variant= (an explicit choice wins).

  THE DIALOG LIVES ON <body>
  --------------------------
  The PDP details column is position:sticky, which creates a containing block -
  a position:fixed dialog inside it gets clipped. The dialog is moved to body on
  init for the same reason sections/mega-shop.liquid moves its panel.

  CHANGING THE GARMENT
  --------------------
  The chart setting is the source of truth: one row per size,
  "SIZE,length,shoulder,chestFlat,sleeve" in cm. All four products on this store
  are the same blank (RU0010), so one chart serves all of them.
*/

function bcffInit(ROOT) {
  var TPL = document.querySelector('[data-bcff-tpl="' + ROOT.getAttribute('data-bcff') + '"]');
  if (!TPL || ROOT.dataset.bound) return;
  ROOT.dataset.bound = '1';

  var CFG = {
    ease:   parseFloat(ROOT.dataset.ease)   || 4,
    lenF:   parseFloat(ROOT.dataset.lenf)   || 0.375,
    border: parseFloat(ROOT.dataset.border),
    memory: ROOT.dataset.remember === 'true',
    /* OPTIONAL. A garment/fit image shown on the result screen only. Absent on
       stores that never set it, so one copy of this asset serves every brand. */
    img:    (ROOT.dataset.img || '').trim(),
    key:    'bcff_v1'
  };
  if (isNaN(CFG.border)) CFG.border = 3;

  /* Garment table, cm, measured FLAT. Chest circumference is 2x the chart.
     Delivered from the block's chart setting as rows of
     SIZE,length,shoulder,chest,sleeve joined with a pipe. */
  var SIZES = [];
  (ROOT.dataset.chart || '').split('|').forEach(function (row) {
    var c = row.split(',');
    if (c.length < 5) return;
    SIZES.push({ k: c[0].trim(), len: +c[1], sh: +c[2], cf: +c[3], sl: +c[4] });
  });
  if (!SIZES.length) return;

  /* THE SAVED SIZE IS ONLY MEANINGFUL FOR THE LADDER IT WAS CHOSEN FROM.
     Sizes get relabelled - BitterClub shifted its whole ladder one step down
     when it stopped trusting the blank supplier's labels - and a visitor who
     had "3XL" remembered from before would silently match nothing, while one
     who had "L" would match a NEW L that is a physically bigger shirt. Both
     failures are invisible: no error, just a wrong answer.

     Keying the store on the ladder itself means any relabel invalidates old
     answers automatically. No version number to remember to bump, and one
     canonical copy of this file still serves every brand. */
  CFG.key = 'bcff:' + SIZES.map(function (s) { return s.k; }).join('-');
  SIZES.forEach(function (s) { s.chest = s.cf * 2; });

  var LADDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

  /* ---------- the questions. every one multiple choice, on purpose ---------- */
  var STEPS = [
    {
      id: 'h',
      q: 'how tall are you?',
      sub: 'this decides length. these run a little short in the body, so height matters more here than it usually would.',
      opts: [
        { v: 160, a: 'under 5′4″',   b: 'under 163 cm' },
        { v: 166, a: '5′4″ – 5′6″', b: '163 – 168 cm' },
        { v: 173, a: '5′7″ – 5′9″', b: '170 – 175 cm' },
        { v: 180, a: '5′10″ – 6′0″', b: '178 – 183 cm' },
        { v: 188, a: '6′1″ – 6′3″', b: '185 – 191 cm' },
        { v: 195, a: '6′4″ and up', b: '193 cm and up' }
      ]
    },
    {
      id: 'w',
      q: 'roughly what do you weigh?',
      sub: 'a rough guess is fine, it only needs to be in the right band. skip it if you would rather not.',
      opts: [
        { v: 55,  a: 'under 130 lb', b: 'under 59 kg' },
        { v: 64,  a: '130 – 150 lb', b: '59 – 68 kg' },
        { v: 72,  a: '150 – 170 lb', b: '68 – 77 kg' },
        { v: 82,  a: '170 – 190 lb', b: '77 – 86 kg' },
        { v: 92,  a: '190 – 215 lb', b: '86 – 98 kg' },
        { v: 104, a: '215 – 240 lb', b: '98 – 109 kg' },
        { v: 115, a: '240 lb and up', b: '109 kg and up' },
        { v: null, a: 'rather not say', b: 'rather not say', quiet: true }
      ]
    },
    {
      id: 'b',
      q: 'how would you describe your build?',
      sub: 'two people at the same height and weight can carry it completely differently.',
      opts: [
        { v: -6, a: 'slim / lean' },
        { v: 0,  a: 'average' },
        { v: 5,  a: 'athletic, broad shoulders' },
        { v: 9,  a: 'fuller through the middle' }
      ]
    },
    {
      id: 'f',
      q: 'how do you like a tee to sit?',
      sub: 'this is the one that changes the answer most. be honest rather than aspirational.',
      opts: [
        { v: -1, a: 'fitted, close to the body',  b2: 'size down' },
        { v: 0,  a: 'regular, just skims',        b2: 'true to size' },
        { v: 1,  a: 'relaxed, room to move',      b2: 'size up' },
        { v: 2,  a: 'oversized, properly baggy',  b2: 'size up twice' }
      ]
    },
    {
      id: 'u',
      q: 'what size tee do you usually buy?',
      sub: 'the size you already own is the best check we have on everything above.',
      opts: [
        { v: 'S' },  { v: 'M' }, { v: 'L' }, { v: 'XL' }, { v: '2XL' }, { v: '3XL' },
        { v: null, a: 'not sure', quiet: true }
      ]
    }
  ];

  /* ---------- engine ---------- */
  function estChest(a) {
    /* RE-ANCHORED 2026-08-27. The first version used 88cm at 175/65 with a
       0.60cm-per-kg slope, which under-read an average frame by 5-7cm and,
       paired with a 10cm ease, made M serve only a 90cm chest - a 35in chest,
       a genuinely slim man. The operator's correction was that M fits an
       AVERAGE 5ft9 guy normally, so M is a normal size on this blank, not a
       small one.

       Anchor: 175cm / 75kg / average build = 97cm chest, which is a real 38in.
       Slope 0.78cm per kg is measured off the 150-210lb range at that height;
       -0.30cm per cm of height because at equal weight a taller frame is
       leaner through the chest. */
    if (a.w == null) {
      if (a.u) {
        var ui = LADDER.indexOf(a.u);
        var mi = SIZES.findIndex(function (s) { return s.k === a.u; });
        if (mi >= 0) return SIZES[mi].chest - CFG.ease;
        /* they buy a size we do not stock (S/XS) - read it off the ladder */
        var mIdx = LADDER.indexOf(SIZES[0].k);
        return SIZES[0].chest - CFG.ease - (mIdx - ui) * 4.5;
      }
      return 97 - 0.30 * (a.h - 175) + a.b;
    }
    return 97 + 0.78 * (a.w - 75) - 0.30 * (a.h - 175) + a.b;
  }

  function sizeByChest(chest) {
    for (var i = 0; i < SIZES.length; i++) {
      if (chest <= SIZES[i].chest - CFG.ease) return i;
    }
    return SIZES.length - 1;
  }

  /* smallest size whose body length clears height * factor. only ever pushes up. */
  function lengthFloor(h) {
    var want = h * CFG.lenF;
    for (var i = 0; i < SIZES.length; i++) {
      if (SIZES[i].len >= want) return i;
    }
    return SIZES.length - 1;
  }

  function clamp(i) { return Math.max(0, Math.min(SIZES.length - 1, i)); }

  function solve(a) {
    var chest = estChest(a);
    var byChest = sizeByChest(chest);
    var floor = lengthFloor(a.h);
    var maxed = chest > SIZES[SIZES.length - 1].chest - CFG.ease;

    /* THE SIZE THEY ALREADY OWN IS THE ANCHOR. The tape only breaks ties. */
    var uIdx = null, belowRange = false;
    if (a.u) {
      var raw = LADDER.indexOf(a.u) - LADDER.indexOf(SIZES[0].k);
      belowRange = raw < 0;          /* they buy an S and we start at M */
      uIdx = clamp(raw);
    }

    var base, pulled = false;
    if (uIdx !== null) {
      base = uIdx;
      /* two or more steps apart means one of them is badly wrong - meet halfway,
         never override a garment they can actually feel */
      if (Math.abs(byChest - uIdx) >= 2) {
        base = clamp(uIdx + (byChest > uIdx ? 1 : -1));
        pulled = true;
      }
    } else {
      base = byChest;
    }

    /* length floor: can only ever add one, and only above the anchor */
    var lengthPushed = false;
    if (floor > base) { base = clamp(base + 1); lengthPushed = true; }

    var pick = clamp(base + a.f);

    /* show two when the tape and the tag disagree by exactly one, or when the
       estimate is sitting on a boundary, or when we had nothing to anchor on */
    var alt = null;
    var edge = Math.abs(chest - (SIZES[clamp(byChest)].chest - CFG.ease));
    if (uIdx !== null && Math.abs(byChest - uIdx) === 1) {
      alt = clamp(pick + (byChest > uIdx ? 1 : -1));
    } else if (byChest < SIZES.length - 1 && edge <= CFG.border) {
      alt = clamp(pick + 1);
    }
    if (lengthPushed && alt === null) alt = clamp(pick - 1);
    if (uIdx === null && a.w == null && alt === null) alt = clamp(pick + 1);
    if (alt === pick) alt = null;
    /* only suppress the alternative when we have genuinely run out of range */
    if (maxed && pick === SIZES.length - 1) alt = null;

    return {
      chest: Math.round(chest),
      pick: pick,
      alt: alt,
      lengthPushed: lengthPushed,
      pulled: pulled,
      maxed: maxed && pick === SIZES.length - 1,
      belowRange: belowRange,
      anchored: uIdx !== null,
      agrees: uIdx !== null && !belowRange && pick === uIdx
    };
  }

  function reason(r, a) {
    var s = SIZES[r.pick];
    var bits = [];
    if (r.maxed) {
      bits.push('the ' + s.k + ' is the biggest we make, so on you it will sit closer to the body than it does in the photos');
    } else if (r.belowRange) {
      bits.push('you buy below the smallest size we cut, so the ' + s.k + ' is where you start here');
    } else if (r.agrees) {
      bits.push('same as the size you already buy, and these run true to that');
    } else if (r.pulled) {
      bits.push('your usual size and your measurements disagreed, so we split the difference');
    } else if (r.anchored) {
      bits.push('started from the size you already own');
    }
    if (r.lengthPushed) {
      bits.push('one up for length, because these run short in the body and you are tall enough to notice');
    }
    if (a.f === -1) bits.push('kept it close to the body like you asked');
    if (a.f === 1)  bits.push('one up so it sits relaxed');
    if (a.f === 2)  bits.push('two up for a properly oversized look');
    bits.push('the ' + s.k + ' measures ' + Math.round(s.chest) + ' cm around the chest and ' + s.len + ' cm long');
    return bits.join('. ') + '.';
  }

  function feels(i, a) {
    var s = SIZES[i];
    var slack = Math.round(s.chest - (a.chestEst || 0));
    if (slack <= 4)  return 'closer to the body, ends higher';
    if (slack <= 10) return 'the intended fit, relaxed but not baggy';
    return 'roomier and longer, leans oversized';
  }

  /* ---------- state ---------- */
  var A = { h: null, w: null, b: null, f: null, u: null };
  var step = 0;
  var metric = false;
  var sheet = null;

  function save(size) {
    if (!CFG.memory) return;
    try { localStorage.setItem(CFG.key, JSON.stringify({ size: size, a: A, t: Date.now() })); } catch (e) {}
  }
  function load() {
    if (!CFG.memory) return null;
    try { return JSON.parse(localStorage.getItem(CFG.key) || 'null'); } catch (e) { return null; }
  }

  /* ---------- selecting the size in Fabric's own picker ---------- */
  /* WHICH OPTION GROUPS HOLD THE SIZE LADDER.
     Normally there is exactly one option literally named "Size". The 2-Pack has
     TWO size runs, named after the colourways - "Black" and "Rose Pink" - so
     nothing matches on the name and the old /size/i filter returned an empty
     list. That made pickSize find nothing, addToBag return false, and the
     result screen's button silently do nothing at all.

     So: match on the option NAME first, and only if that finds nothing fall
     back to matching the radio VALUES against the ladder. Name always wins, so
     a product with a real "Size" option can never be confused by a Color option
     whose values happen to look like sizes. */
  function sizeGroupNames() {
    var all = document.querySelectorAll('variant-picker input[type="radio"][data-option-name]');
    var keys = {};
    SIZES.forEach(function (s) { keys[s.k.toUpperCase()] = 1; });
    var named = [], byValue = [];
    for (var i = 0; i < all.length; i++) {
      var nm = all[i].getAttribute('data-option-name') || '';
      if (/size/i.test(nm)) {
        if (named.indexOf(nm) === -1) named.push(nm);
      } else if (keys[(all[i].value || '').trim().toUpperCase()]) {
        if (byValue.indexOf(nm) === -1) byValue.push(nm);
      }
    }
    return named.length ? named : byValue;
  }

  function sizeInputs() {
    var names = sizeGroupNames();
    var all = document.querySelectorAll('variant-picker input[type="radio"][data-option-name]');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (names.indexOf(all[i].getAttribute('data-option-name') || '') !== -1) out.push(all[i]);
    }
    return out;
  }

  /* Always re-queries the live DOM. Never hold a node across a click. */
  function findInput(name, key) {
    var all = document.querySelectorAll('variant-picker input[type="radio"][data-option-name]');
    for (var i = 0; i < all.length; i++) {
      if ((all[i].getAttribute('data-option-name') || '') !== name) continue;
      if ((all[i].value || '').trim().toUpperCase() === key) return all[i];
    }
    return null;
  }

  /* Sets the size in EVERY size group, one at a time, then calls back.
     Fabric re-renders the picker on each variant change and throws away the
     nodes we were holding, so each group is looked up fresh and the clicks are
     spaced out rather than fired in one pass over a stale list. */
  function pickSizeAll(k, done) {
    var names = sizeGroupNames();
    var key = String(k).toUpperCase();
    var i = 0, any = false;
    (function next() {
      if (i >= names.length) { if (done) done(any); return; }
      var input = findInput(names[i++], key);
      if (input && input.getAttribute('data-option-available') !== 'false') {
        if (!input.checked) input.click();
        any = true;
        setTimeout(next, 120);
      } else {
        next();
      }
    })();
  }

  /* Best-effort synchronous version, used only by the returning-visitor path
     where nothing is added to the cart afterwards. */
  function pickSize(k) {
    var names = sizeGroupNames();
    var key = String(k).toUpperCase();
    var any = false;
    for (var i = 0; i < names.length; i++) {
      var input = findInput(names[i], key);
      if (input && input.getAttribute('data-option-available') !== 'false') {
        if (!input.checked) input.click();
        any = true;
      }
    }
    return any;
  }

  /* Adds by clicking Fabric's REAL add-to-cart button rather than posting to
     /cart/add.js ourselves. That button carries on:click="/handleClick", so
     clicking it runs the theme's own submit, cart drawer, fly-to-cart animation
     and error handling. Re-implementing any of that would drift from the theme.
     The size is selected first and the click is deferred past our own close
     animation so the cart drawer does not open underneath this sheet. */
  function addToBag(k) {
    /* Waits until every size group is actually set before pressing Add. With
       two groups a fixed timer would fire while the second one was still
       unset and add a half-configured variant. */
    pickSizeAll(k, function (ok) {
      if (!ok) return;
      var btn = document.querySelector('add-to-cart-component button[name="add"]');
      if (!btn || btn.disabled) return;
      setTimeout(function () { btn.click(); }, 300);
    });
    return true;
  }

  function offered(k) {
    var ins = sizeInputs();
    for (var i = 0; i < ins.length; i++) {
      if ((ins[i].value || '').trim().toUpperCase() === k.toUpperCase()) return true;
    }
    return false;
  }

  /* ---------- rendering ---------- */
  function el(html) { var d = document.createElement('div'); d.innerHTML = html.trim(); return d.firstElementChild; }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]; }); }

  function dots(body) {
    var d = sheet.querySelector('[data-bcff-dots]');
    d.innerHTML = '';
    for (var i = 0; i < STEPS.length; i++) {
      var c = 'bcff-sheet__dot' + (i === step ? ' is-on' : (i < step ? ' is-done' : ''));
      d.appendChild(el('<span class="' + c + '"></span>'));
    }
    d.style.visibility = step >= STEPS.length ? 'hidden' : '';
  }

  function drawStep() {
    var s = STEPS[step];
    var body = sheet.querySelector('[data-bcff-body]');
    var html = '<p class="bcff-q">' + esc(s.q) + '</p>';
    if (s.sub) html += '<p class="bcff-sub">' + esc(s.sub) + '</p>';
    html += '<div class="bcff-opts">';
    s.opts.forEach(function (o, i) {
      var label = o.a != null ? o.a : o.v;
      var right = '';
      if (o.b) { label = metric ? o.b : o.a; }
      if (o.b2) right = '<span class="bcff-opt__alt">' + esc(o.b2) + '</span>';
      var picked = (A[s.id] === o.v && A[s.id] !== null) ? ' is-picked' : '';
      html += '<button type="button" class="bcff-opt' + (o.quiet ? ' bcff-opt--quiet' : '') + picked +
              '" data-i="' + i + '"><span>' + esc(label) + '</span>' + right + '</button>';
    });
    html += '</div>';
    body.innerHTML = html;

    var foot = sheet.querySelector('.bcff-sheet__foot');
    foot.hidden = !(s.id === 'h' || s.id === 'w');

    body.querySelectorAll('.bcff-opt').forEach(function (btn) {
      btn.addEventListener('click', function () {
        A[s.id] = s.opts[+btn.dataset.i].v;
        step++;
        render();
      });
    });

    sheet.querySelector('[data-bcff-back]').hidden = step === 0;
    dots();
    body.scrollTop = 0;
  }

  function chartHTML(hit) {
    var h = '<table class="bcff-chart"><caption>measured flat, in inches and cm</caption><tr><th></th>';
    SIZES.forEach(function (s) { h += '<th>' + esc(s.k) + '</th>'; });
    h += '</tr>';
    [['length', 'len'], ['shoulder', 'sh'], ['chest (flat)', 'cf'], ['sleeve', 'sl']].forEach(function (r) {
      h += '<tr><td>' + r[0] + '</td>';
      SIZES.forEach(function (s) {
        h += '<td>' + (s[r[1]] / 2.54).toFixed(1) + '″<br>' + s[r[1]] + '</td>';
      });
      h += '</tr>';
    });
    return h + '</table>';
  }

  function drawResult() {
    var r = solve(A);
    A.chestEst = r.chest;
    var body = sheet.querySelector('[data-bcff-body]');
    sheet.querySelector('.bcff-sheet__foot').hidden = true;
    sheet.querySelector('[data-bcff-back]').hidden = false;
    dots();

    var pickK = SIZES[r.pick].k;
    var html = '';

    /* The picture goes FIRST and only here. Putting it on the question steps
       would push the answers below the fold on a phone, and the questions are
       the part that has to stay effortless. */
    if (CFG.img) html += '<img class="bcff-res__img" src="' + esc(CFG.img) + '" alt="" loading="lazy">';

    if (r.alt !== null && offered(SIZES[r.alt].k)) {
      var lo = Math.min(r.pick, r.alt), hi = Math.max(r.pick, r.alt);
      html += '<p class="bcff-res__eyebrow">you are between two</p>';
      html += '<p class="bcff-q" style="margin-bottom:14px">' + esc(SIZES[lo].k) + ' or ' + esc(SIZES[hi].k) + ', and both work</p>';
      html += '<div class="bcff-two">';
      [lo, hi].forEach(function (i) {
        html += '<div class="bcff-card"><span class="bcff-card__k">' + esc(SIZES[i].k) + '</span>' +
                '<span class="bcff-card__d">' + esc(feels(i, A)) + '</span>' +
                '<button type="button" class="bcff-res__cta' + (i === r.pick ? '' : ' bcff-res__cta--ghost') +
                '" data-pick="' + esc(SIZES[i].k) + '">add the ' + esc(SIZES[i].k) + '</button></div>';
      });
      html += '</div>';
      html += '<p class="bcff-res__why" style="margin-top:16px">' + esc(reason(r, A)) + '</p>';
    } else {
      html += '<p class="bcff-res__eyebrow">we’ve got you in a</p>';
      html += '<p class="bcff-res__size">' + esc(pickK) + '</p>';
      html += '<p class="bcff-res__why">' + esc(reason(r, A)) + '</p>';
      html += '<button type="button" class="bcff-res__cta" data-pick="' + esc(pickK) + '">add the ' + esc(pickK) + ' to my bag</button>';
    }

    html += chartHTML(r.pick);
    html += '<button type="button" class="bcff-again" data-bcff-again>start over</button>';
    body.innerHTML = html;
    body.scrollTop = 0;

    body.querySelectorAll('[data-pick]').forEach(function (b) {
      b.addEventListener('click', function () {
        var k = b.getAttribute('data-pick');
        save(k);
        paintSaved(k);
        addToBag(k);      /* falls back to just selecting the size if it fails */
        close();
      });
    });
    body.querySelector('[data-bcff-again]').addEventListener('click', function () {
      A = { h: null, w: null, b: null, f: null, u: null };
      step = 0;
      render();
    });
  }

  function render() { if (step >= STEPS.length) drawResult(); else drawStep(); }

  /* ---------- the saved line next to the button ---------- */
  function paintSaved(k) {
    var s = ROOT.querySelector('[data-bcff-saved]');
    if (!k) { s.hidden = true; return; }
    s.hidden = false;
    s.innerHTML = 'you’re a ' + esc(k) + ' · <button type="button" data-bcff-redo>redo</button>';
    s.querySelector('[data-bcff-redo]').addEventListener('click', function () {
      A = { h: null, w: null, b: null, f: null, u: null };
      step = 0;
      open();
    });
  }

  /* ---------- open / close ---------- */
  function build() {
    if (sheet) return;
    sheet = TPL.content.firstElementChild.cloneNode(true);
    document.body.appendChild(sheet);
    sheet.querySelectorAll('[data-bcff-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    sheet.querySelector('[data-bcff-back]').addEventListener('click', function () {
      if (step > 0) { step--; render(); }
    });
    sheet.querySelector('[data-bcff-units]').addEventListener('click', function () {
      metric = !metric;
      this.textContent = metric ? 'show ft / lb' : 'show cm / kg';
      render();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet && sheet.classList.contains('is-open')) close();
    });
  }

  function open() {
    build();
    sheet.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () { sheet.classList.add('is-in'); });
    render();
  }
  function close() {
    if (!sheet) return;
    sheet.classList.remove('is-in');
    document.body.style.overflow = '';
    setTimeout(function () { sheet.classList.remove('is-open'); }, 260);
  }

  ROOT.querySelector('[data-bcff-open]').addEventListener('click', open);

  /* ---------- returning visitor ---------- */
  var saved = load();
  if (saved && saved.size) {
    paintSaved(saved.size);
    /* an explicit ?variant= in the url is a deliberate choice and always wins */
    if (!/[?&]variant=/.test(location.search)) {
      if (saved.a) A = saved.a;
      /* wait for the picker to hydrate before touching it */
      var tries = 0;
      var iv = setInterval(function () {
        if (sizeInputs().length) { clearInterval(iv); pickSize(saved.size); }
        else if (++tries > 40) clearInterval(iv);
      }, 50);
    }
  }
}

function bcffBoot() {
  var roots = document.querySelectorAll('[data-bcff]');
  for (var i = 0; i < roots.length; i++) bcffInit(roots[i]);
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bcffBoot);
} else {
  bcffBoot();
}
/* Fabric re-renders sections on variant change and in the theme editor. */
document.addEventListener('shopify:section:load', bcffBoot);
document.addEventListener('shopify:block:select', bcffBoot);
